import { get } from '../../utils/httpRequest';
import { shouldSkipTracking } from '../utils/cookie';
import { hashValue } from '../utils/strings';
import { flushConsentNotifications } from './consent';
import { datalayerGlobal } from './datalayerMap';
import { datalayerStorageClient } from './datalayerStorage';
import { emptyDatalayer } from './emptyDatalayer';
import {
  handleTmsDataError,
  tmsEventDataContainsErrors,
  visitDataContainsErrors,
} from './error';
import type {
  Datalayer,
  DatalayerEventPayload,
  DatalayerGlobal,
  DatalayerResponse,
  DatalayerUpdater,
  DatalayerVisitPayload,
  Primitive,
  TMSFetchOptions,
  TrackTMSPageDatalayer,
} from './types';
import {
  flatten,
  getParamFromDatalayer,
  getRememberMeCookie,
  isNilOrEmpty,
} from './utils';

const {
  getDatalayerGlobalParams,
  getPreviousDatalayer,
  storePreviousDatalayer,
  updateDatalayerGlobalParams,
  getClientId,
  getAppTraffic,
} = datalayerStorageClient();

let lastTrackedPage: string | undefined;
let hasLoadedContainerOnce = false;
let pendingVisitPayload: DatalayerVisitPayload | undefined;

export const initializeDatalayer = () => {
  if (!getRememberMeCookie()) return;
  const rememberMePayload: DatalayerVisitPayload = {
    event_category: 'auto-login',
    user_login_mode: 'sso_remember_me_cookie',
  };
  trackVisit(rememberMePayload);
};

/**
 * Resets the last tracked page and the trackVisit gate. Primarily intended for testing purposes.
 * @param {string} [resetValue] - Optional value to reset the last tracked page to.
 * If omitted, the last tracked page will be reset to `undefined`.
 * @remarks This function is for testing only and should not be used in production code.
 */
export const resetLastTrackedTMSPage = (resetValue?: string) => {
  lastTrackedPage = resetValue;
  hasLoadedContainerOnce = false;
  pendingVisitPayload = undefined;
};

/**
 * Initializes the inner TC vars.
 * When it's not available, it tries to get it from the opener tab (if available).
 * @returns {Partial<Datalayer>} - The inner TC vars.
 */
const initInnerTcVars = (): Partial<Datalayer> => {
  try {
    const openerTcVars = (window?.opener as Window)
      ?.tc_vars as Partial<Datalayer>;
    if (openerTcVars && Object.keys(openerTcVars).length) {
      return openerTcVars;
    }
  } catch {
    // Ignore CORS error
  }

  const currentTcVars = window?.tc_vars as Partial<Datalayer> | undefined;
  return currentTcVars && Object.keys(currentTcVars).length
    ? { ...emptyDatalayer, ...currentTcVars }
    : emptyDatalayer;
};

/**
 * Retrieves the previous TC vars from session storage or initializes them if not available.
 * If the TC vars are not found in session storage, it attempts to initialize them from the opener tab or window.tc_vars.
 * Updates the TC vars history in session storage if initialized.
 * @returns {Partial<Datalayer>} - The previous TC vars.
 */
const getPrevTCVars = (): Partial<Datalayer> => {
  let tcVars: Partial<Datalayer> = getPreviousDatalayer();
  if (!tcVars || !Object.keys(tcVars).length) {
    tcVars = initInnerTcVars();
    storePreviousDatalayer(tcVars);
    return tcVars;
  }
  const currentTCVars: Partial<Datalayer> = (window?.tc_vars || {}) as
    | Partial<Datalayer>
    | undefined;
  return {
    ...tcVars,
    ...Object.fromEntries(
      Object.entries(currentTCVars).filter(
        ([, value]) => !isNilOrEmpty(value as string)
      )
    ),
  };
};

/**
 * Creates a datalayer updater object.
 * @param {DatalayerUpdater} parser - The parser function to transform the input item.
 * @returns {Object} - The datalayer updater object.
 * @deprecated In product libraries, use a custom hook from frontend-core
 */
export const createDatalayerUpdater = (parser: DatalayerUpdater) => ({
  /**
   * Updates the datalayer with the provided item.
   * @param {unknown} item - The item to update the datalayer with.
   */
  update(item) {
    const datalayer: Partial<Datalayer> = {
      ...parser(item),
    };
    updateAndSyncDatalayer(datalayer);
  },
});

/**
 * Updates the session-filtered datalayer and synchronizes it with Tag Commander's variables.
 * @param datalayer - the object containing the properties to update.
 */
export const updateAndSyncDatalayer = (datalayer: Partial<Datalayer>) => {
  updateSessionFilteredDatalayer(datalayer);
  syncTcVars(datalayer);
};

/**
 * Removes keys related to product that don't need to be persisted
 * @param datalayer
 */
const updateSessionFilteredDatalayer = (datalayer: Partial<Datalayer> = {}) => {
  const filtered = filterDatalayer(datalayer);
  if (Object.keys(filtered).length) {
    updateDatalayerGlobalParams(filtered);
  }
};

/**
 * Filters the datalayer to keep only the global properties.
 * This is used to keep the datalayer clean and avoid sending unnecessary data.
 * It will remove the properties that are not global and are empty.
 * @param datalayer - the object containing the properties to update.
 * @returns {Partial<DatalayerGlobal>} - the filtered datalayer.
 */
const filterDatalayer = (
  datalayer: Partial<Datalayer> = {}
): Partial<DatalayerGlobal> => {
  return Object.keys(datalayer).reduce(
    (acc: Partial<DatalayerGlobal>, key: string) => {
      const datalayerKey = key as keyof Datalayer;
      if (isTMSVariableGlobal(key) && !isNilOrEmpty(datalayer[datalayerKey])) {
        (acc as Record<string, unknown>)[key] = datalayer[datalayerKey];
      }
      return acc;
    },
    {} as Partial<DatalayerGlobal>
  );
};

/**
 * Fires a datalayer event with the provided data payload.
 * @param {DatalayerEventPayload} data - The data payload for the event.
 * @deprecated In product libraries, use a custom hook from frontend-core
 */
export const fireDataLayerEvent = (data: DatalayerEventPayload): void => {
  void fireEvent(data);
};

/**
 * Persists the visit's user_login_mode (and other fields) so it's observable right away,
 * even if the actual visit event is still queued behind the first container load.
 * @param data - The payload containing visit information for the datalayer.
 */
const persistVisitLoginMode = (data: DatalayerVisitPayload): void => {
  const { event_category: _event_category, user_login_mode, ...rest } = data;
  if (user_login_mode) {
    updateAndSyncDatalayer({ user_login_mode, ...rest });
  }
};

/**
 * Fires the Tag Commander visit event, without any ordering guarantees.
 * @param data - The payload containing visit information for the datalayer.
 */
const fireVisit = (data: DatalayerVisitPayload): void => {
  const { event_category, user_login_mode, ...rest } = data;
  window.tC?.event?.visit?.(undefined, {
    parm: user_login_mode,
    event_category,
    ...rest,
  });
};

/**
 * Tracks a visit event in the datalayer and triggers the Tag Commander visit event.
 * If the funnel's first trackTMSPage/loadContainer call hasn't happened yet, the visit
 * is queued and fired right after it does, so it never precedes the page view event.
 * The persisted user_login_mode prevents subsequent calls for the same visit.
 * @param data - The payload containing visit information for the datalayer.
 */
export const trackVisit = (data: DatalayerVisitPayload): void => {
  if (visitDataContainsErrors(data)) return;

  const hasTrackedLoginMode = Boolean(getParamFromDatalayer('user_login_mode'));
  if (hasTrackedLoginMode) return;

  persistVisitLoginMode(data);

  if (!hasLoadedContainerOnce) {
    pendingVisitPayload ??= data;
    return;
  }
  fireVisit(data);
};

/**
 * Check if a variable is defined as global so it should be kept when
 * the product or the search changes
 * @param name
 * @returns {boolean}
 */
export const isTMSVariableGlobal = (name: string): boolean => {
  return datalayerGlobal.includes(name);
};

/**
 * Checks if the funnel has changed or if the user has exited the funnel.
 * @param prevDatalayer - The previous data layer state.
 * @param tcVars - The current data layer state.
 * @returns {boolean} - True if the funnel has changed or the user has exited the funnel, otherwise false.
 */
const funnelChanged = (
  prevDatalayer: Partial<Datalayer>,
  tcVars: Partial<Datalayer>
) => {
  if (!prevDatalayer || !Object.keys(prevDatalayer).length) {
    return false;
  }
  const { product } = tcVars;
  return (
    product !== undefined &&
    prevDatalayer.product &&
    prevDatalayer.product !== product
  );
};

/**
 * Retrieves client-side attributes to be included in the data layer.
 *
 * @returns {Partial<Datalayer>} An object containing partial data layer attributes:
 * - `ext_ga_client_id`: The Google Analytics client ID retrieved via `getClientId()`.
 * - `page_url`: The current page's URL from `document.location.href`.
 */
const getClientSideAttributes = (): Partial<Datalayer> => {
  return {
    app_traffic: getAppTraffic(),
    ext_ga_client_id: getClientId(),
    page_url: document.location.href,
  };
};

/**
 * Syncs the datalayer with the TC variables.
 * It will keep the previous page name if the funnel has not changed.
 * It will keep the previous value if the variable is not global and the funnel has changed.
 * It will add client side attributes to the datalayer.
 * It will update the window.tc_vars with the new datalayer.
 * It will update the datalayer in the storage.
 */
export const syncTcVars = (tcVars: Partial<Datalayer> = {}) => {
  const prevDatalayer = getPrevTCVars();
  if (tcVars.page_name && prevDatalayer.page_name !== tcVars.page_name) {
    tcVars.page_previous = prevDatalayer.page_name;
  }
  const storedDatalayer = getDatalayerGlobalParams();
  const hasFunnelChanged = funnelChanged(prevDatalayer, tcVars);
  const syncFn = (
    key: string,
    oldValue: Primitive,
    newValue: Primitive
  ): Primitive =>
    newValue ||
    (!hasFunnelChanged || isTMSVariableGlobal(key)
      ? oldValue
      : (emptyDatalayer[key] as Primitive));
  Object.keys(prevDatalayer).forEach((key) => {
    tcVars[key] = syncFn(
      key,
      prevDatalayer[key] as Primitive,
      tcVars[key] as Primitive
    );
  });
  window.tc_vars = {
    ...tcVars,
    ...getClientSideAttributes(),
    ...storedDatalayer,
  } as Partial<Datalayer>;
  storePreviousDatalayer(window.tc_vars as Partial<Datalayer>);
};

/**
 * Syncs the datalayer and reloads the tagCommander container.
 * On the first call, also flushes any trackVisit queued before this ran.
 * @returns {void}
 */
export const loadContainer = (tcVars: Partial<Datalayer> = {}): void => {
  const { page_name } = tcVars;
  if ((page_name && page_name === lastTrackedPage) || shouldSkipTracking()) {
    flushConsentNotifications();
    return;
  }
  syncTcVars(tcVars);
  window?.tC?.container?.reload?.({
    events: { page: [{}, {}] },
  });
  lastTrackedPage = page_name;

  if (!hasLoadedContainerOnce) {
    hasLoadedContainerOnce = true;
    if (pendingVisitPayload) {
      fireVisit(pendingVisitPayload);
      pendingVisitPayload = undefined;
    }
  }
  flushConsentNotifications();
};

/**
 * Tracks a page view using the Tag Commander TMS (Tag Management System).
 *
 * This function handles errors in the provided data, updates the session-specific
 * filtered data layer, and optionally fetches additional base information for the
 * data layer before loading the Tag Commander container.
 *
 * @param data - The data layer object containing information to be tracked.
 * @param options - Optional configuration for the tracking operation.
 * @param options.skipFetch - If `true`, skips fetching additional base information
 *                            for the data layer. Defaults to `false`.
 * @returns A promise that resolves when the tracking operation is complete.
 */
export const trackTMSPage = async (
  data: TrackTMSPageDatalayer,
  { skipFetch }: TMSFetchOptions = {}
): Promise<void> => {
  handleTmsDataError(data);
  let tcVars: Partial<Datalayer> = { ...data };
  if (!skipFetch) {
    updateSessionFilteredDatalayer(data);
    const baseInfoDatalayer = filterDatalayer(await fetchDatalayer());
    tcVars = { ...baseInfoDatalayer, ...data };
  }
  loadContainer(tcVars);
};

/**
 * Fetches the datalayer from the backend and updates the TC vars.
 * A datalayer contains only basic/general information is needed for the TMS.
 * @returns {Promise<void>}
 */
export const fetchDatalayer = async (): Promise<Datalayer> => {
  try {
    const response: DatalayerResponse = await get(
      '/travel/service/tms/datalayer/general?product='
    );
    return flatten(response as Record<string, unknown>) as unknown as Datalayer;
  } catch (_) {
    // avoid fetch error
    return {} as Datalayer;
  }
};

/**
 * Fires a datalayer event with the provided category, key, and value.
 * If the key is 'customer_email', it hashes the value before updating the datalayer.
 * Synchronizes the TC variables and reloads the Tag Commander container.
 *
 * @returns {void}
 * @param data
 */
export const fireEvent = async (data: DatalayerEventPayload): Promise<void> => {
  const errors = tmsEventDataContainsErrors(data);
  if (errors) return;
  let datalayer: Partial<Datalayer> = {};
  const { customer_email } = data;
  if (customer_email) {
    const customer_email_hash = await hashValue(customer_email);
    datalayer = { customer_email_hash };
  }
  const eventData = { ...data, ...datalayer };
  Object.entries(data).forEach(([key, value]) => {
    if (key !== 'event_category' && !isNilOrEmpty(value)) {
      datalayer[key] = value;
    }
  });
  updateAndSyncDatalayer(datalayer);
  if (shouldSkipTracking()) return;
  window.tC?.event?.click?.(this, eventData);
};
